/* eslint-disable max-len */
import { Component, OnInit, Injector } from '@angular/core';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { CountryShared } from './components/shared/models/interface/country.interface';
import { environment } from '../environments/environment';
import Package from '../../package.json';
import { NavigationEnd, Router } from '@angular/router';
import { CacheService } from 'src/app/components/shared/sharedServices/cache.service';
import { StorageService } from './components/shared/sharedServices/storage.service';
import { StorageClassifier } from './components/shared/models/interface/storageClassifier.enum';
import { CookieService } from 'ngx-cookie-service';
import { MuCookieService } from './components/shared/sharedServices/mu-cookie.service';
import { UserDataService } from './components/shared/sharedServices/user-data.service';
import { PixelService } from 'ngx-pixels';
import { Meta } from '@angular/platform-browser';
import { IndexedDbService } from './components/shared/sharedServices/indexed-db.service';
import { PointsUserService } from './components/shared/sharedServices/points-user.service';
import { firstValueFrom } from 'rxjs';
import { UserData } from './components/shared/sharedModules/auth/models/userData.interface';
import { PermissionOperationsOption } from './components/shared/sharedModules/permission/keys/permissionOperations';
import { PermissionService } from './components/shared/sharedModules/permission/permission.service';

declare let gtag: (
  property: string,
  value: any,
  configs: any
) => {
  /** */
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
})
export class AppComponent implements OnInit {
  title = 'mu-web';
  countryList: CountryShared[];
  items: MenuItem[];
  public appVersion: string;

  // Services
  translateService: TranslateService;
  router: Router;
  cacheService: CacheService;
  storageService: StorageService;
  cookieService: CookieService;
  muCookieService: MuCookieService;
  config: PrimeNGConfig;
  userDataService: UserDataService;
  pixel: PixelService;
  meta: Meta;
  loadedPoint: boolean = false;
  indexedDbService: IndexedDbService;
  pointsUserService: PointsUserService;
  private permissionService: PermissionService;

  constructor(private injector: Injector) {
    this.translateService = this.injector.get<TranslateService>(TranslateService);
    this.router = this.injector.get<Router>(Router);
    this.cacheService = this.injector.get<CacheService>(CacheService);
    this.storageService = this.injector.get<StorageService>(StorageService);
    this.cookieService = this.injector.get<CookieService>(CookieService);
    this.muCookieService = this.injector.get<MuCookieService>(MuCookieService);
    this.config = this.injector.get<PrimeNGConfig>(PrimeNGConfig);
    this.userDataService = this.injector.get<UserDataService>(UserDataService);
    this.pixel = this.injector.get<PixelService>(PixelService);
    this.meta = this.injector.get<Meta>(Meta);
    this.indexedDbService = this.injector.get<IndexedDbService>(IndexedDbService);
    this.pointsUserService = this.injector.get<PointsUserService>(PointsUserService);
    this.permissionService = this.injector.get<PermissionService>(PermissionService);
    this.initData();
  }

  /**
   * Load init data
   */
  initData(): void {
    this.appVersion = Package.version;
    this.countryList = environment.countries;
    this.preloadLanguages();
    this.router.events.subscribe(async event => {
      if (event instanceof NavigationEnd) {
        this.checkLastVersion();

        if (environment.nonTrackingDomain !== window.location.hostname) {
          this.gtagFactory();
          this.facebookPixel();
        }

        if (environment.nonTrackingDomain === window.location.hostname) {
          this.pixel.remove();
        }
        this.checkLoadPointsUser();
        
      }
    });
  }

  gtagFactory() {
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + environment.googleAnalyticsId;
    script.type = 'text/javascript';
    script.async = true;

    document.head.appendChild(script);

    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args) {
      (window as any).dataLayer.push(arguments);
    }

    gtag('js', new Date());
  }

  facebookPixel() {
    if (!this.meta.getTag('facebook-domain-verification')) {
      this.meta.addTag(
        { name: 'facebook-domain-verification', content: 'ibp56tahcng1ypwv9wpqum7y0flhqi' },
        true
      );
      this.pixel.initialize();
    }
  }

  ngOnInit() {
    this.items = [
      {
        label: '',
        items: [
          { label: 'CEDI / Tiendas', icon: 'pi pi-fw pi-plus' },
          { label: 'Programar Entrega Masiva', icon: 'pi pi-fw pi-plus' },
          { label: 'Historial', icon: 'pi pi-fw pi-plus' },
          { label: 'Integración', icon: 'pi pi-fw pi-plus' },
          { label: 'Ayuda', icon: 'pi pi-fw pi-question' },
        ],
      },
    ];
  }

  
  /**
   * Method to check last version
   */
  checkLastVersion(): void {
    let muVersion = this.cookieService.get('mu-version');
    if (
      !muVersion &&
      (window.location.pathname.includes('crear-cuenta') ||
        window.location.pathname.includes('ext'))
    ) {
      this.muCookieService.setCookie('mu-version', this.appVersion, environment.timeVerifyVersion);
      muVersion = this.cookieService.get('mu-version');
    }

    if (muVersion !== this.appVersion) {
      sessionStorage.clear();
      this.storageService.clear(
        StorageClassifier.DATA,
        StorageClassifier.FILTER,
        StorageClassifier.TEMPORAL,
        StorageClassifier.PERSISTENT_FILTER
      );
      this.cacheService.clearCache();
      this.cookieService.deleteAll();
      this.muCookieService.setCookie('mu-version', this.appVersion, environment.timeVerifyVersion);
      location.reload();
    }
  }

  async checkLoadPointsUser() {
    const token = this.muCookieService.getCookie('token-mu');
    if (token.length === 0) {
      await this.removeDataPointsUser();
      return;
    }
    const data = localStorage.getItem('mu-data') as string;
    if (!data) {
      await this.removeDataPointsUser();
      return;
    }
    if (this.router.url.includes('login')) {
      await this.removeDataPointsUser();
      return;
    }
    const permission = await this.permissionService.canAccess(
      PermissionOperationsOption.DASHBOARD_COMMON_LOGIN
    );
    if (!permission) {
      await this.removeDataPointsUser();
      return;
    }

    if (this.loadedPoint) {
      return;
    }
    try {
      const dataUser: UserData = JSON.parse(
        JSON.parse(window.atob(this.cookieService.get('mu-data')))
      );
      this.userDataService.setTokenLocalInStorage();
      this.pointsUserService.loadPointsUser(dataUser);
      this.loadedPoint = true;
    } catch (error) {
      console.error('Error loading user data:', error);
      this.removeDataPointsUser();
      return;
    }
  }

  async removeDataPointsUser() {
    this.pointsUserService.cancelLoadOrders();
    this.pointsUserService.cancelLoad$.next();
    this.pointsUserService.cancelLoad$.complete();
    this.cookieService.delete('progress_points');
    this.cookieService.delete('progress_points_complete');
    await firstValueFrom(this.indexedDbService.clearStore('points_store'));
    this.loadedPoint = false;
  }

  /**
   * method to load languages files on refresh page
   */
  preloadLanguages(): void {
    const locale = localStorage.getItem('location');
    if (!locale) {
      return this.translateService.setDefaultLang(this.countryList[0].languageKey);
    }
    const country = this.countryList.find(c => c.geoName === locale);
    const langKey = country ? country.languageKey : this.countryList[0].languageKey;
    this.translateService.setDefaultLang(langKey);
    this.translateService.get('primeng').subscribe(res => this.config.setTranslation(res));
  }
}
